import { Info, Pause, Play, TriangleAlert } from "lucide-react";
import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  RADAR_ATTRIBUTION_URL,
  RADAR_LICENCE_URL,
  WIND_ATTRIBUTION_URL,
  WIND_LICENCE_URL,
} from "@divespots/contracts";
import type { Locale } from "@divespots/contracts";
import { ARROW_LENGTH_PX, ARROW_WEIGHT_PX, WIND_CLASS_LIMITS_KMH, windVar } from "./ramp.ts";
import type { WindClass } from "./ramp.ts";
import { formatRunTime, formatWindTime } from "./time.ts";
import { readInfoOpen, writeInfoOpen } from "./storage.ts";
import type { WindReading } from "./readout.ts";
import { precipVar } from "../precip/raster.ts";
import type { PrecipReading } from "../precip/readout.ts";
import { WeatherCredit } from "./WeatherCredit.tsx";
import { weatherLine } from "./weatherLine.ts";
import { formatHourLabel } from "../precip/time.ts";
import type { WindTime } from "./useWindTime.ts";
import type { FrameState } from "../radar/frames.ts";
import type { RadarReading } from "../radar/raster.ts";
import type { BarStep } from "../radar/steps.ts";

interface Props {
  time: WindTime;
  status: "loading" | "ready" | "unavailable";
  locale: Locale;
  /** Wind overlay on: its readout or arrow legend, the "Jetzt"/clock labels. Off = precipitation alone. */
  windOn?: boolean;
  /** Arrows (reduced motion) get the colour/length legend; particles get the readout. */
  mode: "particles" | "arrows";
  /** Wind at the open spot, else the map centre; `wind` null = no data there. */
  readout: { place: string; wind: WindReading | null } | null;
  /** Wind is on but its forecast failed while precipitation is shown: say so instead of going quiet. */
  windFailed?: boolean;
  /** Present while the precipitation overlay is on; `reading` null = no sum for that hour or place. */
  precip?:
    | {
        status: "loading" | "ready" | "unavailable";
        readout: { place: string; reading: PrecipReading | null } | null;
      }
    | undefined;
  /** Model run (initialisation time), when the API knows it. */
  runAt?: string | null | undefined;
  /**
   * The observed radar joined into this bar (precipitation on). `active`: the slider is
   * index-based over `steps` (measured, then forecast); otherwise `unavailable` adds a notice to
   * the plain forecast bar.
   */
  radar?: RadarBarProps | undefined;
  /** The short credit row; off on phones, where the map footer carries it. */
  showCredit?: boolean;
}

export interface RadarBarProps {
  active: boolean;
  unavailable: boolean;
  steps: readonly BarStep[];
  index: number;
  jetzt: number;
  divider: number;
  playing: boolean;
  now: number;
  onIndex: (i: number) => void;
  onTogglePlay: () => void;
  /** State of the selected radar step's picture (undefined at forecast steps). */
  frame: FrameState | undefined;
  /** Radar value at the open spot or map centre; null while the picture is not there. */
  readout: { place: string; reading: RadarReading | null } | null;
  /** Wind is on and this radar step lies before the wind field's first time. */
  windMissing: boolean;
  /** Wind is drawn at this step (its credit joins the radar credit). */
  windShown: boolean;
}

/** The slider thumb's width: the divider is placed relative to the track minus one thumb. */
const THUMB_PX = 16;

/** Height the phone bar (and the credit under it) takes above the sheet: the pin must land above it. */
export const WIND_BAR_RESERVE_PX = 146;

/** With both overlays the combined readout often takes a second line on a phone. */
export const BOTH_BAR_RESERVE_PX = 158;

/** The credit row inside the bar (tablets, desktop): about two caption lines. */
export const BAR_CREDIT_ROW_PX = 36;

/** Under 380 px the radar stamp takes its own line above the slider. */
export const RADAR_BAR_EXTRA_PX = 35;

/** Same for the one-line chip of landscape phones. */
export const WIND_CHIP_RESERVE_PX = 56;

const CLASS_LABELS = [
  `<${WIND_CLASS_LIMITS_KMH[0]}`,
  `${WIND_CLASS_LIMITS_KMH[0]}–${WIND_CLASS_LIMITS_KMH[1]}`,
  `${WIND_CLASS_LIMITS_KMH[1]}–${WIND_CLASS_LIMITS_KMH[2]}`,
  `≥${WIND_CLASS_LIMITS_KMH[2]}`,
] as const;

/** Glass bar under the wind overlay: forecast hour slider, speed legend, "model forecast" note. */
export function WindTimeBar({
  time,
  status,
  locale,
  runAt,
  mode,
  readout,
  windOn = true,
  windFailed = false,
  precip,
  radar,
  showCredit = true,
}: Props) {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(readInfoOpen);
  const infoButton = useRef<HTMLButtonElement>(null);
  const more = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const setOpen = (open: boolean) => {
    writeInfoOpen(open);
    setInfoOpen(open);
  };
  // Esc inside the bar closes the panel first: the sheet listens on window and must not also drop a detent
  useEffect(() => {
    if (!infoOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !sectionRef.current?.contains(document.activeElement)) return;
      e.preventDefault();
      if (more.current?.contains(document.activeElement)) infoButton.current?.focus();
      writeInfoOpen(false);
      setInfoOpen(false);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [infoOpen]);
  const radarMode = !!radar?.active && radar.index >= 0;
  const { range } = time;
  const now = radarMode ? radar.now : time.now;
  const step = radarMode ? radar.steps[radar.index]! : null;
  const stepLabel = (s: BarStep, jetzt: boolean): string => {
    if (s.kind === "radar") {
      const at = formatWindTime(s.t, now, locale);
      return t(jetzt ? "radar.nowMeasured" : "radar.measuredAt", { time: at });
    }
    return t("radar.forecastAt", {
      time: windOn ? formatWindTime(s.t, now, locale) : formatHourLabel(s.t, now, locale),
    });
  };
  const label = radarMode
    ? stepLabel(step!, radar.index === radar.jetzt)
    : time.t === null
      ? ""
      : !windOn
        ? formatHourLabel(time.t, now, locale)
        : time.t === time.defaultT
          ? t("wind.now")
          : formatWindTime(time.t, now, locale);
  const atRadar = radarMode && step!.kind === "radar";
  const both = windOn && !!precip;
  const prefix = both ? "precip.labelBoth" : windOn ? "wind.label" : "precip.label";
  const windMissing = !!radar?.windMissing && atRadar;
  const line = weatherLine(t, {
    locale,
    now,
    windOn,
    atRadar,
    both,
    windMissing,
    wind: readout,
    precip,
    radar: radar ? { frame: radar.frame, readout: radar.readout } : undefined,
  });
  const notices = [
    radar?.unavailable && !radarMode ? t("radar.unavailable") : null,
    windFailed ? t("wind.unavailable") : null,
  ].filter((x): x is string => x !== null);
  const toggleInfo = () => setOpen(!infoOpen);
  const infoLabel = t(both ? "precip.infoBoth" : windOn ? "wind.info" : "precip.info");
  return (
    <section
      ref={sectionRef}
      className="wind-bar ds-glass"
      aria-label={t(prefix)}
      data-info={infoOpen ? "open" : "closed"}
    >
      {radarMode || (status === "ready" && range && time.t !== null) ? (
        <>
          <span className="wind-bar__chip ds-text-caption-1">
            {label} · {t(atRadar ? "radar.chip" : "wind.chip")}
          </span>
          <div className="wind-bar__row">
            {radarMode && radar.steps.some((x) => x.kind === "radar") ? (
              <button
                type="button"
                className="wind-bar__play ds-btn ds-btn--icon ds-btn--plain"
                aria-label={t(radar.playing ? "radar.pause" : "radar.play")}
                aria-pressed={radar.playing}
                onClick={radar.onTogglePlay}
              >
                {radar.playing ? (
                  <Pause aria-hidden="true" size={20} strokeWidth={1.75} />
                ) : (
                  <Play aria-hidden="true" size={20} strokeWidth={1.75} />
                )}
              </button>
            ) : null}
            {radarMode ? (
              <>
                <div
                  className="wind-bar__track"
                  style={
                    radar.divider > 0
                      ? ({
                          "--divider": `calc(${THUMB_PX / 2}px + (100% - ${THUMB_PX}px) * ${
                            (radar.divider - 0.5) / (radar.steps.length - 1)
                          })`,
                        } as CSSProperties)
                      : undefined
                  }
                >
                  <input
                    id="wind-time"
                    type="range"
                    className="wind-bar__slider"
                    aria-label={t("wind.time")}
                    aria-valuetext={label}
                    min={0}
                    max={radar.steps.length - 1}
                    step={1}
                    value={radar.index}
                    onChange={(e) => radar.onIndex(Number(e.currentTarget.value))}
                  />
                  {radar.divider > 0 ? (
                    <span className="wind-bar__divider" aria-hidden="true" />
                  ) : null}
                </div>
                <output
                  className="wind-bar__stamp ds-text-footnote"
                  htmlFor="wind-time"
                  title={label}
                  aria-live={radar.playing ? "off" : undefined}
                >
                  {label}
                </output>
              </>
            ) : (
              <>
                <input
                  id="wind-time"
                  type="range"
                  className="wind-bar__slider"
                  aria-label={t("wind.time")}
                  aria-valuetext={label}
                  min={range!.min}
                  max={range!.max}
                  step={time.step}
                  value={time.t!}
                  onChange={(e) => time.setT(Number(e.currentTarget.value))}
                />
                <output className="wind-bar__time ds-text-footnote" htmlFor="wind-time">
                  {label}
                </output>
              </>
            )}
          </div>
          <div className="wind-bar__line">
            {line ? (
              <p className="wind-bar__readout ds-text-footnote" role="status" title={line}>
                {line}
              </p>
            ) : null}
            {notices.length > 0 ? (
              <button
                type="button"
                className="wind-bar__alert ds-btn ds-btn--icon ds-btn--plain"
                aria-label={notices.join(" · ")}
                title={notices.join(" · ")}
                aria-expanded={infoOpen}
                aria-controls="wind-more"
                onClick={toggleInfo}
              >
                <TriangleAlert aria-hidden="true" size={18} strokeWidth={1.75} />
              </button>
            ) : null}
            <button
              ref={infoButton}
              type="button"
              className="wind-bar__info ds-btn ds-btn--icon ds-btn--plain"
              aria-label={infoLabel}
              title={t(infoOpen ? "weather.hide" : "weather.show")}
              aria-expanded={infoOpen}
              aria-controls="wind-more"
              onClick={toggleInfo}
            >
              <Info aria-hidden="true" size={18} strokeWidth={1.75} />
            </button>
          </div>
          {showCredit ? (
            <p className="wind-bar__credit ds-text-caption-1">
              <WeatherCredit atRadar={atRadar} windShown={!!radar?.windShown} />
            </p>
          ) : null}
          {/* the notices are announced even while the panel is shut */}
          <p className="wind-bar__sr" role="status">
            {notices.join(". ")}
          </p>
          <div className="wind-bar__more" id="wind-more" ref={more} hidden={!infoOpen}>
            {notices.map((n) => (
              <p key={n} className="wind-bar__notice ds-text-subheadline">
                <TriangleAlert aria-hidden="true" size={16} strokeWidth={1.75} /> {n}
              </p>
            ))}
            {windOn && mode === "arrows" && !windMissing ? (
              <ul className="wind-bar__legend ds-text-caption-1" aria-label={t("wind.legend")}>
                {CLASS_LABELS.map((text, c) => (
                  <li key={text}>
                    <span
                      className="wind-bar__swatch"
                      aria-hidden="true"
                      style={{
                        background: `var(${windVar(c as WindClass)})`,
                        width: ARROW_LENGTH_PX[c as WindClass],
                        height: ARROW_WEIGHT_PX[c as WindClass],
                      }}
                    />
                    {text}
                  </li>
                ))}
                <li className="wind-bar__unit">km/h</li>
              </ul>
            ) : null}
            {precip ? (
              <ul
                className="wind-bar__legend precip-legend ds-text-caption-1"
                aria-label={t("precip.legend")}
              >
                {([0, 1, 2, 3] as const).map((c) => (
                  <li key={c}>
                    <span
                      className="precip-legend__swatch"
                      aria-hidden="true"
                      style={{ background: `var(${precipVar(c)})` }}
                    />
                    {t(`precip.class${c}`)}
                  </li>
                ))}
                <li className="wind-bar__unit">mm/h</li>
              </ul>
            ) : null}
            {atRadar && precip ? (
              <p className="wind-bar__model ds-text-caption-1">{t("radar.legendNote")}</p>
            ) : null}
            <p className="wind-bar__model ds-text-caption-1">
              {[
                windOn && !windMissing ? t("wind.model") : null,
                precip ? t(atRadar ? "radar.model" : "precip.model") : null,
              ]
                .filter(Boolean)
                .join(" · ")}
              {runAt && !(atRadar && !radar?.windShown)
                ? ` · ${t("wind.run", { time: formatRunTime(runAt, locale) })}`
                : ""}
            </p>
            {atRadar ? (
              <p className="wind-bar__model ds-text-caption-1">{t("radar.artefacts")}</p>
            ) : null}
            {atRadar ? (
              <p className="wind-bar__model ds-text-caption-1">
                {t("radar.creditVia")}{" "}
                <a href={RADAR_ATTRIBUTION_URL} target="_blank" rel="noopener noreferrer">
                  MeteoSwiss
                </a>{" "}
                (
                <a href={RADAR_LICENCE_URL} target="_blank" rel="noopener noreferrer">
                  CC BY 4.0
                </a>
                ), {t("radar.reprojected")}
              </p>
            ) : null}
            {!atRadar || radar?.windShown ? (
              <p className="wind-bar__model ds-text-caption-1">
                {t(
                  atRadar
                    ? "wind.creditVia"
                    : both
                      ? "precip.creditViaBoth"
                      : windOn
                        ? "wind.creditVia"
                        : "precip.creditVia",
                )}{" "}
                <a href={WIND_ATTRIBUTION_URL} target="_blank" rel="noopener noreferrer">
                  Open-Meteo
                </a>{" "}
                (
                <a href={WIND_LICENCE_URL} target="_blank" rel="noopener noreferrer">
                  CC BY 4.0
                </a>
                ), {t("wind.interpolated")}
              </p>
            ) : null}
          </div>
        </>
      ) : (
        <p className="wind-bar__status ds-text-subheadline" role="status">
          {status === "loading"
            ? t(windOn ? "wind.loading" : "precip.loading")
            : t(windOn ? "wind.unavailable" : "precip.unavailable")}
        </p>
      )}
    </section>
  );
}
